import React, { useState } from 'react';
import {
  Button,
  Collapse,
  Form,
  Message,
  Space,
  Tabs,
  Tag,
} from '@bedrock/components';
import classNames from 'classnames/bind';
import styles from './index.less';
import CollapseTablePanel from '#components/CollapseTablePanel';
import FormItemDescriptions from '#components/DescriptionsFormItem';
import { Copy2, Delete, Plus, SignIn } from '@bedrock/icons-react';
import { useImportFileModal } from '#features/importFileModal';
import SupplierVisible from '@mpc/common/src/features/SupplierVisible';
import { useDemandOptions } from '#features/useDemandOptions';
import CustomInspectionFields from './CustomInspectionDetail/CustomInspectionFields';
import { ProCard } from '@bedrock/eed-react';
import { get } from 'lodash';
import SFormItem from '@mpc/common/src/components/SForm/SFormItem';
import DropdownButtonGroup from '#components/DropdownButtonGroup';
import CustomInspectionTemplateSelect from './CustomInspectionTemplateSelect';
import { useMemoizedFn } from 'ahooks';
import { demandSourceMatchTemplateDetail } from '#services/api';

const cx = classNames.bind(styles);

interface Props {
  tabsModel?: boolean;
  addable?: boolean;
  deletable?: boolean;
  name?: string | string[];
  view?: boolean;
  formData?: any;
  changeFormData?: (name, value) => void;
  style?: React.CSSProperties;
  className?: string;
  hasSavePermission?: boolean;
  l3_category_id?: number;
  productId?: number;
  proCardProps?: any;
}

const DEFAULT_ACTIVE_KEYS = Array.from({ length: 20 }, (_, i) => i);
const ALL_ACTIVE_KEYS = Array.from({ length: 1000 }, (_, i) => i);

const CustomInspection = (props: Props) => {
  const {
    hasSavePermission = false,
    view,
    tabsModel,
    addable = true,
    deletable = true,
    name = 'custom_inspection_list',
    formData,
    changeFormData,
    style,
    className,
    l3_category_id,
    productId,
  } = props;
  const [activeKeys, setActiveKeys] = useState<number[]>(
    tabsModel ? ALL_ACTIVE_KEYS : DEFAULT_ACTIVE_KEYS
  );
  const [activeTabKey, setActiveTabKey] = useState('0');

  const { optionsMap } = useDemandOptions({
    keys: ['main_platform'],
    params: {
      l3_category_id,
    },
  });

  const customInspectionData = get(formData, name);

  return (
    <Form.List name={name} initialValue={[]}>
      {(fields, { add, remove }) => {
        if (view && !fields?.length) {
          return null;
        }
        return (
          <div style={style} className={cx('custom-inspection', className)}> 
            <ProCard
              title={
                (
                  <SupplierVisible>
                    定制排查
                    {tabsModel && fields?.length && `（${fields?.length}）`}
                  </SupplierVisible>
                ) as any
              }
              level={2}
              noPadding
              {...props?.proCardProps}
            >
              {tabsModel && (
                <Tabs
                  activeKey={activeTabKey}
                  type="card"
                  onChange={(v) => {
                    setActiveTabKey(v);
                  }}
                  className="m-b-8"
                >
                  {fields?.map((field, index) => {
                    return (
                      <Tabs.TabPane
                        tab={`定制排查${index + 1}`}
                        key={index + ''}
                      ></Tabs.TabPane>
                    );
                  })}
                </Tabs>
              )}
              {fields?.map((field, index) => {
                if (tabsModel && index !== Number(activeTabKey)) {
                  return null;
                }
                const { key, ...fieldRest } = field;
                const fieldConfig = (fieldType: string) => {
                  return {
                    ...fieldRest,
                    formItemKey: [field.name, fieldType],
                    name: [field.name, fieldType],
                    fieldKey: [field.key, fieldType],
                    view,
                  };
                };
                const mainPlatform =
                  customInspectionData?.[index]?.main_platform;

                return (
                  <Collapse
                    activeKey={activeKeys.includes(index) ? [1] : undefined}
                    key={index}
                    onChange={(key) => {
                      setActiveKeys(
                        activeKeys.includes(index)
                          ? activeKeys.filter((i) => i !== index)
                          : [...activeKeys, index]
                      );
                    }}
                  >
                    <CollapseTablePanel
                      className={tabsModel ? 'mb-[0]' : ''}
                      style={{ width: '100%' }}
                      header={
                        <div className={cx('inner-header')}>
                          <div>
                            <Space size="xSmall">
                              <span>定制排查{index + 1}</span>
                              {!!mainPlatform && view && (
                                <Tag
                                  color="blue"
                                  size="xSmall"
                                  text={mainPlatform}
                                />
                              )}
                            </Space>
                          </div>
                          <div>
                            <Space size={0}>
                              {addable && !view && (
                                <Button
                                  type="text-subtle"
                                  size="xSmall"
                                  icon={<Copy2 />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const listData = customInspectionData;
                                    const copyData = JSON.parse(
                                      JSON.stringify(listData[index])
                                    );
                                    add(copyData, index + 1);
                                  }}
                                >
                                  复制
                                </Button>
                              )}

                              {deletable && !view && (
                                <Button
                                  type="text-subtle"
                                  size="xSmall"
                                  icon={<Delete />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    remove(field.name);
                                  }}
                                  danger
                                >
                                  删除
                                </Button>
                              )}
                            </Space>
                          </div>
                        </div>
                      }
                      key={1}
                    >
                      {view ? (
                        <SFormItem
                          {...fieldConfig('custom_inspection_fields')}
                          noStyle
                          render={() => {
                            return (
                              <CustomInspectionFields
                                type={'preview'}
                                defaultReferTemplateId={
                                  customInspectionData?.[index]?.template_id
                                }
                              />
                            );
                          }}
                        />
                      ) : (
                        <FormItemDescriptions
                          lightBorder
                          className={cx('collapse-content-table')}
                          column={2}
                          items={[
                            {
                              ...fieldConfig('main_platform'),
                              label: '合作主平台',
                              required: true,
                              componentClass: 'select',
                              span: 1,
                              elementProps: {
                                options: optionsMap?.main_platform?.options,
                                multiple: false,
                                showReset: true,
                                // showConfirm: true,
                                // selectFormat: {
                                //   optionsToValue: (value, options) => {
                                //     const option =
                                //       options?.find(
                                //         (item) => item.value === value
                                //       ) || {};
                                //     return {
                                //       ...option,
                                //       id: option?.value,
                                //     };
                                //   },
                                //   valueToSelectKey: (value) => {
                                //     return value?.value || value?.id;
                                //   },
                                // },
                              },
                            },
                            {
                              ...fieldConfig('template_id'),
                              label: null,
                              labelStyle: {
                                display: 'none',
                              },
                              required: false,
                              componentClass: 'input',
                              span: 1,
                              style: {
                                display: 'none',
                              },
                              elementProps: {
                                style: {
                                  display: 'none',
                                },
                              },
                            },
                            {
                              ...fieldConfig('custom_inspection_fields'),
                              label: '定制排查条件',
                              required: true,
                              componentClass: 'select',
                              span: 2,
                              validateTrigger: ['onChange, onSubmit'],
                              dependencies: [
                                [
                                  ...(Array.isArray(name) ? name : [name]),
                                  field.name,
                                  'main_platform',
                                ],
                              ],
                              elementProps: {},
                              watchChildFormValidate: true,
                              render: () => {
                                const mainPlatform =
                                  customInspectionData?.[index]?.main_platform;
                                return (
                                  <CustomInspectionFields
                                    type={'edit'}
                                    // showReferTemplateButton={true}
                                    // showSaveTemplateButton={true}
                                    // showSaveAsNewTemplateButton={true}
                                    hasSavePermission={hasSavePermission}
                                    defaultReferTemplateId={
                                      customInspectionData?.[index]?.template_id
                                    }
                                    defaultFilters={{
                                      main_platform: mainPlatform
                                        ? [mainPlatform]
                                        : undefined,
                                    }}
                                    fixedFilters={{
                                      product_id: productId
                                        ? [productId]
                                        : undefined,
                                    }}
                                    onSelectTemplate={(template) => {
                                      changeFormData?.(
                                        [
                                          ...(Array.isArray(name)
                                            ? name
                                            : [name]),
                                          field.name,
                                          'template_id',
                                        ],
                                        template.id
                                      );
                                    }}
                                  />
                                );
                              },
                            },
                          ]}
                        />
                      )}
                    </CollapseTablePanel>
                  </Collapse>
                );
              })}
              {!view && addable && (
                <div style={{ display: 'flex', width: 150 }}>
                  <DropdownButtonGroup
                    coverButtonProps={{
                      size:"medium",
                      type:"checked",
                      icon: <Plus />,
                      children: '添加定制排查',
                      block: true,
                    }}
                    items={[
                      {
                        show: true,
                        key: 'menuAdd',
                        buttonProps: {
                          children: '手工添加',
                          onClick: () => { add({}) },
                        }
                      },
                      {
                        show: true,
                        key: 'referTemplate',
                        buttonProps: {
                          children: '引用模板',
                          onClick: () => {
                            CustomInspectionTemplateSelect.show({
                              fixedFilters: {
                                product_id: productId
                                  ? [productId]
                                  : undefined,
                              },
                              onOk: (ids) => { 
                                const id = ids?.[0];
                                if (id) {
                                  demandSourceMatchTemplateDetail({ id })
                                    .then((res: any) => {
                                      if (res.code === 1) {
                                        const data = res.data.demand_source_match_template;
                                        add?.({ custom_inspection_fields: data.demand_source_match_template_field_entities || [] });
                                      }
                                    })
                                    .catch((error: any) => {
                                      console.error('获取详情失败:', error);
                                    });
                                }
                              },
                            });
                          },
                        },
                      }
                    ]}
                  />
                </div>
              )}
            </ProCard>
          </div>
        );
      }}
    </Form.List>
  );
};

export default CustomInspection;
